import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { HCaptcha } from '../components/HCaptcha';

interface LoginPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { signIn, loading, user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);

  // Vérifier si on arrive avec un lien expiré
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setShowExpiredMessage(true);
    }
  }, [searchParams]);

  // Rediriger si l'utilisateur est déjà connecté - VERSION SIMPLE
  useEffect(() => {
    const handleRedirect = async () => {
      if (!loading && user) {
        
        try {
          const { data: profileData, error: profileError } = await api.profiles.getByUserId(user.id);
          
          if (profileError || !profileData) {
            showToast('Créons votre profil pour commencer !', 'success');
            navigate('/create-profile');
          } else {
            navigate('/profile');
          }
        } catch (err) {
          showToast('Créons votre profil !', 'success');
          navigate('/create-profile');
        }
      }
    };
    
    handleRedirect();
  }, [user, loading, navigate, showToast]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      showToast('Veuillez remplir tous les champs', 'error');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showToast('Veuillez entrer une adresse email valide', 'error');
      return;
    }

    if (!captchaToken) {
      showToast('Veuillez valider le captcha de sécurité', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const { user, error} = await signIn(formData.email, formData.password, captchaToken);

      if (error) {
        // Messages d'erreur en français
        let errorMessage = 'Erreur lors de la connexion';
        
        switch (error.message) {
          case 'Invalid login credentials':
            errorMessage = 'Email ou mot de passe incorrect';
            break;
          case 'Email not confirmed':
            errorMessage = 'Veuillez confirmer votre email avant de vous connecter';
            break;
          case 'Too many requests':
            errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
            break;
          default:
            errorMessage = error.message;
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      if (user) {
        showToast('Connexion réussie ! Bienvenue.', 'success');
        
        // Vérification simple : l'utilisateur a-t-il un profil ?
        setTimeout(async () => {
          try {
            const { data: profileData, error: profileError } = await api.profiles.getByUserId(user.id);
            
            if (profileError) {
              showToast('Erreur lors de la vérification du profil.', 'error');
              navigate('/create-profile');
            } else if (profileData) {
              navigate('/profile');
            } else {
              showToast('Créons votre profil !', 'success');
              navigate('/create-profile');
            }
          } catch (err) {
            showToast('Créons votre profil !', 'success');
            navigate('/create-profile');
          }
        }, 500);
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est déjà connecté, rediriger vers son profil
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Vous êtes déjà connecté-e ! Redirection...</p>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-sign-in-alt text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Connexion
          </h2>
          <p className="text-gray-600">
            Connectez-vous à votre compte tuteur
          </p>
        </div>

        {/* Message lien expiré */}
        {showExpiredMessage && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
            <div className="flex items-start">
              <i className="fas fa-info-circle text-blue-500 text-xl mr-3 mt-0.5"></i>
              <div>
                <p className="font-semibold text-blue-800">Veuillez vous connecter à présent</p>
                <p className="text-sm text-blue-700 mt-1">
                  Si vous avez oublié votre mot de passe,{' '}
                  <Link 
                    to="/forgot-password"
                    className="underline font-semibold hover:text-blue-900"
                  >
                    cliquez ici
                  </Link>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-envelope text-[#002c46] mr-2"></i>
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                placeholder="votre.email@exemple.com"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-lock text-[#002c46] mr-2"></i>
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  placeholder="••••••••"
                  data-lpignore="true"
                  data-form-type="other"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            {/* Mot de passe oublié */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[#002c46] hover:text-[#001a2a] font-medium"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            {/* hCaptcha - Protection anti-bot */}
            <div className="space-y-3">
              <div className={`p-4 rounded-lg border-2 transition-all ${
                captchaToken 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}>
                {!captchaToken && (
                  <HCaptcha
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => {
                      setCaptchaToken('');
                      showToast('Erreur de vérification. Veuillez réessayer.', 'error');
                    }}
                  />
                )}
                
                {captchaToken ? (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-3">
                      <i className="fas fa-check text-white text-3xl"></i>
                    </div>
                    <p className="text-lg font-bold text-green-700 mb-1">
                      ✅ Vérification réussie !
                    </p>
                    <p className="text-sm text-green-600">
                      Vous pouvez maintenant vous connecter
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 text-center mt-2">
                    <i className="fas fa-shield-alt mr-1"></i>
                    Protection contre les robots automatiques
                  </p>
                )}
              </div>
            </div>

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isSubmitting || !captchaToken}
              className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#003d5c] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={!captchaToken ? 'Veuillez valider le captcha pour continuer' : ''}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Connexion en cours...
                </>
              ) : !captchaToken ? (
                <>
                  <i className="fas fa-lock mr-2"></i>
                  Validez le captcha pour continuer
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt mr-2"></i>
                  Se connecter
                </>
              )}
            </button>
            {!captchaToken && !isSubmitting && (
              <p className="text-xs text-orange-600 text-center mt-2">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                Le bouton sera activé après validation du captcha
              </p>
            )}
          </form>

          {/* Divider */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Pas encore de compte ?
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center w-full py-3 px-4 border border-[#002c46] text-[#002c46] font-semibold rounded-lg hover:bg-[#002c46]/5 transition-colors"
              >
                <i className="fas fa-user-plus mr-2"></i>
                Créer un compte tuteur
              </Link>
            </div>
          </div>

          {/* Retour */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
      </div>
  );
}; 